import { useId, useMemo, useState } from "react";
import type { PoprawkaAdmin } from "@shared/recenzjeTypy.js";
import type { StanZasobu } from "../../hooks/useZasob.js";
import { api, ApiError } from "../../lib/api.js";
import {
  BEZ_FILTROW,
  OPCJE_FILTRA_WERDYKTU,
  SLOWA_WERDYKTU,
  WARIANTY_WERDYKTU,
  czyFiltrAktywny,
  etykietaRodzajuPoprawki,
  filtrujPoprawki,
  komunikatBleduRecenzji,
  metaPoprawki,
  rozmowyPoprawek,
  tytulPoprawki,
  type FiltrPoprawekWidoku,
} from "../../lib/recenzje.js";
import { zapiszBlobJakoPlik } from "../../lib/zapiszPlik.js";
import { IconPobierz } from "../icons/index.js";
import { Badge } from "../ui/Badge.js";
import { Button } from "../ui/Button.js";
import { Card } from "../ui/Card.js";
import { Field } from "../ui/Field.js";
import { Select } from "../ui/Select.js";
import { useToast } from "../ui/Toast.js";
import { Tooltip } from "../ui/Tooltip.js";
import { StanBledu, StanLadowania, StanPusty } from "../ui/stany.js";

/**
 * Karta "Poprawki": uwagi recenzentow do ocen modelu. Filtr rozmowy i werdyktu dziala na liscie, ktora panel i tak ma
 * (jeden request, instant), a ten sam filtr jedzie do serwera tylko przy "Eksport CSV", zeby plik zgadzal sie z tym, co
 * widac na ekranie.
 *
 * Cztery stany (P-6) plus piaty, "pusto po filtrze", ktory ma wlasne wyjscie ("Wyczysc filtry"). Werdykt ma ZAWSZE slowa
 * (ST-3, A-7): kolor plakietki jest dodatkiem.
 *
 * Komentarz i wersja recenzenta to tresc od osoby z zewnatrz: wstawiamy ja jako tekst (React escapuje), z zachowaniem
 * podzialu wierszy.
 */

export function KartaPoprawek({ zasob }: { zasob: StanZasobu<PoprawkaAdmin[]> }): React.JSX.Element {
  const toast = useToast();
  const idBazowe = useId();
  const [filtr, setFiltr] = useState<FiltrPoprawekWidoku>(BEZ_FILTROW);
  const [eksport, setEksport] = useState(false);
  const { dane, blad, odswiez } = zasob;
  const teraz = new Date();

  const opcjeRozmow = useMemo(
    () => [{ wartosc: "", etykieta: "Wszystkie" }, ...rozmowyPoprawek(dane ?? [])],
    [dane],
  );
  // Rozmowa, ktora zniknela z danych po odswiezeniu, nie moze zostac w filtrze: lista bylaby pusta bez wyjasnienia.
  const rozmowaWFiltrze = opcjeRozmow.some((o) => o.wartosc === filtr.rozmowa) ? filtr.rozmowa : "";
  const aktywnyFiltr = useMemo<FiltrPoprawekWidoku>(
    () => ({ rozmowa: rozmowaWFiltrze, werdykt: filtr.werdykt }),
    [rozmowaWFiltrze, filtr.werdykt],
  );
  const widoczne = useMemo(() => (dane ? filtrujPoprawki(dane, aktywnyFiltr) : []), [dane, aktywnyFiltr]);

  async function eksportuj(): Promise<void> {
    if (eksport) return;
    setEksport(true);
    try {
      const { blob, nazwaPliku } = await api.recenzjePoprawkiCsv({
        ...(rozmowaWFiltrze ? { rozmowa: rozmowaWFiltrze } : {}),
        ...(filtr.werdykt ? { werdykt: filtr.werdykt } : {}),
      });
      zapiszBlobJakoPlik(blob, nazwaPliku);
      toast.sukces("Plik CSV pobrany.");
    } catch (err) {
      toast.blad(komunikatBleduRecenzji(err instanceof ApiError ? err.code : "siec"));
    } finally {
      setEksport(false);
    }
  }

  const brakDanych = dane === null || dane.length === 0;
  const przyciskCsv = (
    <Button wariant="secondary" zajety={eksport} wylaczony={brakDanych} onClick={() => void eksportuj()}>
      {!eksport && <IconPobierz />}
      {eksport ? "Przygotowuję plik..." : "Eksport CSV"}
    </Button>
  );

  return (
    <Card
      tytul="Poprawki"
      akcje={brakDanych ? <Tooltip tekst="Nie ma jeszcze poprawek do wyeksportowania.">{przyciskCsv}</Tooltip> : przyciskCsv}
    >
      {dane === null && blad !== null ? (
        <StanBledu
          tytul="Nie udało się wczytać poprawek"
          opis={komunikatBleduRecenzji(blad)}
          akcja={
            <Button wariant="secondary" onClick={odswiez}>
              Spróbuj ponownie
            </Button>
          }
        />
      ) : dane === null ? (
        <StanLadowania tekst="Wczytuję poprawki..." />
      ) : dane.length === 0 ? (
        <StanPusty
          tytul="Nie ma jeszcze żadnych poprawek"
          opis="Gdy recenzent oceni wskazania modelu albo dopisze uwagę do wypowiedzi, jego werdykty, komentarze, własne wersje i uwagi pojawią się tutaj."
        />
      ) : (
        <>
          <div className="recenzje-filtry">
            <Field etykieta="Rozmowa" id={`${idBazowe}-rozmowa`}>
              {(a) => (
                <Select
                  {...a}
                  wartosc={rozmowaWFiltrze}
                  opcje={opcjeRozmow}
                  onZmiana={(rozmowa) => setFiltr((f) => ({ ...f, rozmowa }))}
                />
              )}
            </Field>
            <Field etykieta="Werdykt" id={`${idBazowe}-werdykt`}>
              {(a) => (
                <Select
                  {...a}
                  wartosc={filtr.werdykt}
                  opcje={OPCJE_FILTRA_WERDYKTU}
                  onZmiana={(werdykt) => setFiltr((f) => ({ ...f, werdykt }))}
                />
              )}
            </Field>
          </div>

          {widoczne.length === 0 ? (
            <StanPusty
              tytul="Brak poprawek dla tych filtrów"
              opis="Zmień rozmowę albo werdykt, żeby zobaczyć więcej."
              akcja={
                <Button wariant="secondary" onClick={() => setFiltr(BEZ_FILTROW)}>
                  Wyczyść filtry
                </Button>
              }
            />
          ) : (
            <ul className="rowlist" aria-label={czyFiltrAktywny(aktywnyFiltr) ? "Poprawki po filtrach" : "Poprawki"}>
              {widoczne.map((p) => {
                const rodzaj = etykietaRodzajuPoprawki(p.rodzaj, p.numerWypowiedzi);
                return (
                  <li className="rowlist__row recenzje-poprawka" key={p.id}>
                    <div className="recenzje-poprawka__glowa">
                      <strong className="recenzje-poprawka__tytul">{tytulPoprawki(p)}</strong>
                      {rodzaj && <Badge>{rodzaj}</Badge>}
                      {p.parametr && <Badge>{p.parametr}</Badge>}
                      {p.werdykt && (
                        <Badge wariant={WARIANTY_WERDYKTU[p.werdykt]} kropka>
                          {SLOWA_WERDYKTU[p.werdykt]}
                        </Badge>
                      )}
                    </div>
                    {p.komentarz && <p className="recenzje-poprawka__tekst">{p.komentarz}</p>}
                    {p.wariant && (
                      <div className="recenzje-poprawka__wersja">
                        <p className="recenzje-poprawka__wersja-etykieta">Wersja recenzenta</p>
                        <p className="recenzje-poprawka__tekst">{p.wariant}</p>
                      </div>
                    )}
                    <p className="recenzje-poprawka__meta">{metaPoprawki(p, teraz)}</p>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </Card>
  );
}
